import { useCallback, useEffect, useMemo, useState } from 'react'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import {
  applyCapitalContributionsSync,
  getWaterfallSettings,
  previewCapitalContributionsSync,
  updateWaterfallSetting,
} from '@/services/waterfall'
import type {
  CapitalAccountSeed,
  CapitalContributionsSyncPreviewResponse,
  MemberCode,
} from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'
import { usePageAccess } from '@/hooks/usePageAccess'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  formatCurrencyField,
  parseDistributionAmount,
} from '@/utils/waterfallCalculator'
import {
  transfersFromConfig,
  transfersToConfig,
  type TransferRow,
} from './capitalTransfersConfig'
import {
  contributionsFromConfig,
  contributionsReadOnlyRowsFromConfig,
  contributionsToConfig,
  memberContributionTotals,
  overallContributionTotal,
  type ContributionEditorRow,
} from './capitalContributionsConfig'
import {
  addSyncIgnore,
  parseSyncIgnores,
  removeSyncIgnore,
  hasNonzeroProposedAmount,
  type CapitalContributionsSyncIgnore,
} from './capitalContributionsSyncIgnores'
import {
  preferredOwnershipFromConfig,
  preferredOwnershipToConfig,
  type PreferredOwnershipRow,
} from './preferredOwnershipConfig'
import WaterfallTabHeader from './WaterfallTabHeader'

const PREFERRED_OWNERSHIP_EARLIEST_DATE = '2025-04-01'

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

type JoinDates = Record<MemberCode, string>

const emptyJoinDates = (): JoinDates =>
  MEMBERS.reduce((acc, m) => {
    acc[m] = ''
    return acc
  }, {} as JoinDates)

function joinDatesFromConfig(value: unknown): JoinDates {
  const out = emptyJoinDates()
  if (!value || typeof value !== 'object') return out
  const byMember = ((value as CapitalAccountSeed).by_member ?? {}) as Record<
    string,
    { join_date?: string }
  >
  for (const m of MEMBERS) {
    const jd = byMember[m]?.join_date
    out[m] = typeof jd === 'string' ? jd : ''
  }
  return out
}

function joinDatesToPayload(dates: JoinDates): Record<string, unknown> {
  const by_member: Record<string, { join_date: string }> = {}
  for (const m of MEMBERS) {
    const join = dates[m]?.trim()
    if (join) by_member[m] = { join_date: join }
  }
  return { by_member }
}

export default function CapitalSettings() {
  const { canEdit } = usePageAccess('distributions')
  const [joinDates, setJoinDates] = useState<JoinDates>(emptyJoinDates)
  const [savedJoinDates, setSavedJoinDates] = useState<JoinDates>(emptyJoinDates)
  const [contributions, setContributions] = useState<ContributionEditorRow[]>([])
  const [savedContributions, setSavedContributions] = useState<ContributionEditorRow[]>([])
  const [contributionsConfig, setContributionsConfig] = useState<unknown>(null)
  const [capitalContributionsLastSyncedAt, setCapitalContributionsLastSyncedAt] = useState<
    string | null
  >(null)
  const [editingContributions, setEditingContributions] = useState(false)
  const [syncPreview, setSyncPreview] = useState<CapitalContributionsSyncPreviewResponse | null>(
    null,
  )
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false)
  const [syncIgnores, setSyncIgnores] = useState<CapitalContributionsSyncIgnore[]>([])
  const [syncIgnoreActionLoading, setSyncIgnoreActionLoading] = useState(false)
  const [syncApplying, setSyncApplying] = useState(false)
  const [transfers, setTransfers] = useState<TransferRow[]>([])
  const [savedTransfers, setSavedTransfers] = useState<TransferRow[]>([])
  const [preferredOwnership, setPreferredOwnership] = useState<PreferredOwnershipRow[]>([])
  const [savedPreferredOwnership, setSavedPreferredOwnership] = useState<PreferredOwnershipRow[]>(
    [],
  )
  const [loading, setLoading] = useState(true)
  const [savingSeed, setSavingSeed] = useState(false)
  const [savingContributions, setSavingContributions] = useState(false)
  const [savingTransfers, setSavingTransfers] = useState(false)
  const [savingPreferredOwnership, setSavingPreferredOwnership] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [seedWarning, setSeedWarning] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getWaterfallSettings()
      const seedItem = data.items.find((i) => i.key === 'capital_account_seed')
      const contribItem = data.items.find((i) => i.key === 'capital_contributions')
      const transfersItem = data.items.find((i) => i.key === 'capital_transfers')
      const ownershipItem = data.items.find((i) => i.key === 'preferred_ownership')
      const parsedJoins = joinDatesFromConfig(seedItem?.value)
      const parsedContribs = contributionsFromConfig(contribItem?.value)
      const parsedTransfers = transfersFromConfig(transfersItem?.value)
      const parsedOwnership = preferredOwnershipFromConfig(ownershipItem?.value)
      setJoinDates(parsedJoins)
      setSavedJoinDates(parsedJoins)
      setContributions(parsedContribs)
      setSavedContributions(parsedContribs)
      setContributionsConfig(contribItem?.value ?? null)
      setCapitalContributionsLastSyncedAt(data.capital_contributions_last_synced_at ?? null)
      setTransfers(parsedTransfers)
      setSavedTransfers(parsedTransfers)
      setPreferredOwnership(parsedOwnership)
      setSavedPreferredOwnership(parsedOwnership)
    } catch (e) {
      setError(formatApiError(e, 'Failed to load settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const seedDirty = useMemo(
    () => JSON.stringify(joinDates) !== JSON.stringify(savedJoinDates),
    [joinDates, savedJoinDates],
  )
  const contributionsDirty = useMemo(
    () => JSON.stringify(contributions) !== JSON.stringify(savedContributions),
    [contributions, savedContributions],
  )
  const transfersDirty = useMemo(
    () => JSON.stringify(transfers) !== JSON.stringify(savedTransfers),
    [transfers, savedTransfers],
  )
  const preferredOwnershipDirty = useMemo(
    () => JSON.stringify(preferredOwnership) !== JSON.stringify(savedPreferredOwnership),
    [preferredOwnership, savedPreferredOwnership],
  )

  const jrJoinDate = joinDates.JR?.trim() || ''

  const contributionsLastSyncedLabel = useMemo(() => {
    if (!capitalContributionsLastSyncedAt) return null
    const parsed = new Date(capitalContributionsLastSyncedAt)
    if (Number.isNaN(parsed.getTime())) return capitalContributionsLastSyncedAt
    return parsed.toLocaleString()
  }, [capitalContributionsLastSyncedAt])

  const readOnlyContributionRows = useMemo(
    () => contributionsReadOnlyRowsFromConfig(contributionsConfig),
    [contributionsConfig],
  )
  const contributionMemberTotals = useMemo(
    () => memberContributionTotals(readOnlyContributionRows),
    [readOnlyContributionRows],
  )
  const contributionGrandTotal = useMemo(
    () => overallContributionTotal(readOnlyContributionRows),
    [readOnlyContributionRows],
  )

  const openSyncPreview = async () => {
    setSyncPreviewLoading(true)
    try {
      const [preview, settings] = await Promise.all([
        previewCapitalContributionsSync(),
        getWaterfallSettings(),
      ])
      setSyncPreview(preview)
      const ignoresItem = settings.items.find(
        (item) => item.key === 'capital_contributions_sync_ignores',
      )
      setSyncIgnores(parseSyncIgnores(ignoresItem?.value))
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to preview QuickBooks sync'))
    } finally {
      setSyncPreviewLoading(false)
    }
  }

  const persistIgnoresAndRefetchPreview = async (ignores: CapitalContributionsSyncIgnore[]) => {
    setSyncIgnoreActionLoading(true)
    try {
      const response = await updateWaterfallSetting('capital_contributions_sync_ignores', ignores)
      const ignoresItem = response.items.find(
        (item) => item.key === 'capital_contributions_sync_ignores',
      )
      setSyncIgnores(parseSyncIgnores(ignoresItem?.value))
      const preview = await previewCapitalContributionsSync()
      setSyncPreview(preview)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update sync ignores'))
      throw e
    } finally {
      setSyncIgnoreActionLoading(false)
    }
  }

  const ignoreSyncRow = async (date: string, member: MemberCode, amount: number) => {
    await persistIgnoresAndRefetchPreview(
      addSyncIgnore(syncIgnores, { date, member, amount }),
    )
  }

  const unignoreSyncRow = async (date: string, member: string) => {
    await persistIgnoresAndRefetchPreview(removeSyncIgnore(syncIgnores, date, member))
  }

  const applySyncPreview = async () => {
    if (!syncPreview) return
    setSyncApplying(true)
    try {
      const response = await applyCapitalContributionsSync(syncPreview.proposed)
      const contribItem = response.items.find((i) => i.key === 'capital_contributions')
      const parsed = contributionsFromConfig(contribItem?.value)
      setContributions(parsed)
      setSavedContributions(parsed)
      setContributionsConfig(contribItem?.value ?? null)
      setCapitalContributionsLastSyncedAt(response.capital_contributions_last_synced_at ?? null)
      setEditingContributions(false)
      setSyncPreview(null)
      toast.success('Capital contributions synced from QuickBooks.')
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to apply QuickBooks sync'))
    } finally {
      setSyncApplying(false)
    }
  }

  const saveSeed = async () => {
    setSavingSeed(true)
    setError(null)
    setSeedWarning(null)
    try {
      const response = await updateWaterfallSetting(
        'capital_account_seed',
        joinDatesToPayload(joinDates),
      )
      const warning = response.warnings?.[0]
      if (warning) setSeedWarning(warning)
      const parsed = joinDatesFromConfig(
        response.items.find((i) => i.key === 'capital_account_seed')?.value,
      )
      setJoinDates(parsed)
      setSavedJoinDates(parsed)
      toast.success('Member join dates saved.')
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save join dates'))
    } finally {
      setSavingSeed(false)
    }
  }

  const saveContributions = async () => {
    setSavingContributions(true)
    setError(null)
    try {
      const response = await updateWaterfallSetting(
        'capital_contributions',
        contributionsToConfig(contributions),
      )
      const parsed = contributionsFromConfig(
        response.items.find((i) => i.key === 'capital_contributions')?.value,
      )
      setContributions(parsed)
      setSavedContributions(parsed)
      setContributionsConfig(
        response.items.find((i) => i.key === 'capital_contributions')?.value ?? null,
      )
      setCapitalContributionsLastSyncedAt(response.capital_contributions_last_synced_at ?? null)
      setEditingContributions(false)
      toast.success('Capital contributions saved.')
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save contributions'))
    } finally {
      setSavingContributions(false)
    }
  }

  const saveTransfers = async () => {
    setSavingTransfers(true)
    setError(null)
    try {
      const response = await updateWaterfallSetting('capital_transfers', transfersToConfig(transfers))
      const parsed = transfersFromConfig(
        response.items.find((i) => i.key === 'capital_transfers')?.value,
      )
      setTransfers(parsed)
      setSavedTransfers(parsed)
      toast.success('Capital transfers saved.')
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save transfers'))
    } finally {
      setSavingTransfers(false)
    }
  }

  const savePreferredOwnership = async () => {
    setSavingPreferredOwnership(true)
    setError(null)
    try {
      const response = await updateWaterfallSetting(
        'preferred_ownership',
        preferredOwnershipToConfig(preferredOwnership),
      )
      const parsed = preferredOwnershipFromConfig(
        response.items.find((i) => i.key === 'preferred_ownership')?.value,
      )
      setPreferredOwnership(parsed)
      setSavedPreferredOwnership(parsed)
      toast.success('Preferred ownership saved.')
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save preferred ownership'))
    } finally {
      setSavingPreferredOwnership(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading capital settings…</p>
  }

  return (
    <div className="w-full space-y-8">
      <WaterfallTabHeader
        title="Settings"
        description="Member join dates and capital contributions for income allocation."
      />

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {seedWarning && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {seedWarning}
        </p>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Member join dates</h3>
          <p className="mt-1 text-sm text-gray-600">
            Each member&apos;s capital account starts at $0 on their join date. KB, LVW, TR, and SM
            are profits-interest members (no capital contribution at joining). JR&apos;s capital
            contribution is entered below and should use her join date.
          </p>
        </div>

        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Member</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Join date</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Notes</th>
              </tr>
            </thead>
            <tbody>
              {MEMBERS.map((m) => (
                <tr key={m} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium">{m}</td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      disabled={!canEdit}
                      value={joinDates[m] ?? ''}
                      onChange={(e) =>
                        setJoinDates((prev) => ({ ...prev, [m]: e.target.value }))
                      }
                      className="w-full max-w-xs rounded border border-gray-200 px-2 py-1 disabled:bg-gray-50"
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {m === 'JR'
                      ? 'Capital member — add contribution below'
                      : 'Profits-interest — starts at $0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canEdit && (
          <button
            type="button"
            onClick={() => void saveSeed()}
            disabled={!seedDirty || savingSeed}
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 btn-primary"
            style={{ background: 'var(--gradient-primary)' }}
          >
            {savingSeed ? 'Saving…' : 'Save join dates'}
          </button>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Capital contributions</h3>
          <p className="mt-1 text-sm text-gray-600">
            Member contributions by date. For JR, enter the contribution that happens on her join date
            {jrJoinDate ? ` (${jrJoinDate})` : ''}. Other members typically have none.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {contributionsLastSyncedLabel
              ? `Last synced ${contributionsLastSyncedLabel}.`
              : 'Never synced.'}
          </p>
        </div>

        {!editingContributions ? (
          <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
            <table className="table-forecast min-w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Member</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Running total</th>
                  <th className="text-right">Member running</th>
                </tr>
              </thead>
              <tbody>
                {readOnlyContributionRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-sm text-gray-500">
                      No contributions configured.
                    </td>
                  </tr>
                ) : (
                  readOnlyContributionRows.map((row, index) => (
                    <tr key={`${row.date}-${row.member}-${index}`}>
                      <td>{row.date}</td>
                      <td>{row.member}</td>
                      <td className="text-right tabular-nums">{currencyFmt.format(row.amount)}</td>
                      <td className="text-right tabular-nums">
                        {currencyFmt.format(row.runningTotal)}
                      </td>
                      <td className="text-right tabular-nums">
                        {currencyFmt.format(row.memberRunningTotal)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {readOnlyContributionRows.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-gray-200 font-medium">
                    <td colSpan={2} className="text-sm text-gray-700">
                      Totals
                    </td>
                    <td className="text-right tabular-nums">{currencyFmt.format(contributionGrandTotal)}</td>
                    <td />
                    <td className="text-right text-xs text-gray-600">
                      {MEMBERS.filter((m) => contributionMemberTotals[m] > 0.005).map((m) => (
                        <div key={m}>
                          {m}: {currencyFmt.format(contributionMemberTotals[m])}
                        </div>
                      ))}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        ) : (
          <div className="space-y-2">
            {contributions.map((row, index) => (
              <div key={`${row.date}-${row.member}-${index}`} className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  disabled={!canEdit}
                  value={row.date}
                  onChange={(e) =>
                    setContributions((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, date: e.target.value } : r)),
                    )
                  }
                  className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
                />
                <select
                  disabled={!canEdit}
                  value={row.member}
                  onChange={(e) =>
                    setContributions((prev) =>
                      prev.map((r, i) =>
                        i === index ? { ...r, member: e.target.value as MemberCode } : r,
                      ),
                    )
                  }
                  className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
                >
                  {MEMBERS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={!canEdit}
                  value={row.amount}
                  placeholder="$0.00"
                  onChange={(e) =>
                    setContributions((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, amount: e.target.value } : r)),
                    )
                  }
                  onBlur={() => {
                    const parsed = parseDistributionAmount(row.amount)
                    if (parsed === null) return
                    const formatted = formatCurrencyField(parsed)
                    setContributions((prev) =>
                      prev.map((r, i) =>
                        i === index ? { ...r, amount: formatted || '$0.00' } : r,
                      ),
                    )
                  }}
                  className="w-40 rounded border border-gray-200 px-2 py-1 text-right text-sm tabular-nums disabled:bg-gray-50"
                />
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="p-1 text-gray-500 hover:text-red-600"
                    onClick={() => setContributions((prev) => prev.filter((_, i) => i !== index))}
                    aria-label="Remove row"
                  >
                    <MinusIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {!editingContributions ? (
              <>
                <Button
                  type="button"
                  gradient
                  disabled={syncPreviewLoading}
                  onClick={() => void openSyncPreview()}
                >
                  {syncPreviewLoading ? 'Loading preview…' : 'Sync from QuickBooks'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditingContributions(true)}>
                  Edit manually
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setContributions(savedContributions)
                    setEditingContributions(false)
                  }}
                >
                  Cancel edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setContributions((prev) => [
                      ...prev,
                      { date: jrJoinDate, member: 'JR', amount: '' },
                    ])
                  }
                >
                  <PlusIcon className="h-4 w-4" />
                  Add row
                </Button>
                <Button
                  type="button"
                  gradient
                  onClick={() => void saveContributions()}
                  disabled={!contributionsDirty || savingContributions}
                >
                  {savingContributions ? 'Saving…' : 'Save contributions'}
                </Button>
              </>
            )}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Capital transfers</h3>
          <p className="mt-1 text-sm text-gray-600">
            Member-to-member capital account transfers by date (e.g. unit purchases between
            members).
          </p>
        </div>

        <div className="space-y-2">
          {transfers.map((row, index) => (
            <div
              key={`${row.date}-${row.from}-${row.to}-${index}`}
              className="flex flex-wrap items-center gap-2"
            >
              <input
                type="date"
                disabled={!canEdit}
                value={row.date}
                onChange={(e) =>
                  setTransfers((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, date: e.target.value } : r)),
                  )
                }
                className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
              />
              <select
                disabled={!canEdit}
                value={row.from}
                onChange={(e) =>
                  setTransfers((prev) =>
                    prev.map((r, i) =>
                      i === index ? { ...r, from: e.target.value as MemberCode } : r,
                    ),
                  )
                }
                className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
              >
                {MEMBERS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-500">→</span>
              <select
                disabled={!canEdit}
                value={row.to}
                onChange={(e) =>
                  setTransfers((prev) =>
                    prev.map((r, i) =>
                      i === index ? { ...r, to: e.target.value as MemberCode } : r,
                    ),
                  )
                }
                className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
              >
                {MEMBERS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <input
                type="text"
                inputMode="decimal"
                disabled={!canEdit}
                value={row.amount}
                placeholder="$0.00"
                onChange={(e) =>
                  setTransfers((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, amount: e.target.value } : r)),
                  )
                }
                onBlur={() => {
                  const parsed = parseDistributionAmount(row.amount)
                  if (parsed === null) return
                  const formatted = formatCurrencyField(parsed)
                  setTransfers((prev) =>
                    prev.map((r, i) =>
                      i === index ? { ...r, amount: formatted || '$0.00' } : r,
                    ),
                  )
                }}
                className="w-40 rounded border border-gray-200 px-2 py-1 text-right text-sm tabular-nums disabled:bg-gray-50"
              />
              {row.from === row.to && (
                <span className="text-xs text-red-600">From and to must differ</span>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setTransfers((prev) => prev.filter((_, i) => i !== index))}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                  aria-label="Remove row"
                >
                  <MinusIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                setTransfers((prev) => [
                  ...prev,
                  { date: '', from: 'JR', to: 'KB', amount: '' },
                ])
              }
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add row
            </button>
            <button
              type="button"
              onClick={() => void saveTransfers()}
              disabled={!transfersDirty || savingTransfers}
              className="rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 btn-primary"
              style={{ background: 'var(--gradient-primary)' }}
            >
              {savingTransfers ? 'Saving…' : 'Save transfers'}
            </button>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Preferred ownership</h3>
          <p className="mt-1 text-sm text-gray-600">
            Effective-dated ownership shares for splitting preferred return (0–1 decimal per member).
            Shares on each date must sum to 1. Earliest date must be {PREFERRED_OWNERSHIP_EARLIEST_DATE}.
          </p>
        </div>

        <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
          <table className="table-forecast min-w-full">
            <thead>
              <tr>
                <th>Effective date</th>
                <th>Member</th>
                <th className="text-right">Share</th>
                {canEdit ? <th className="w-10" /> : null}
              </tr>
            </thead>
            <tbody>
              {preferredOwnership.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 4 : 3} className="text-sm text-gray-500">
                    No ownership rows configured.
                  </td>
                </tr>
              ) : (
                preferredOwnership.map((row, index) => (
                  <tr key={`${row.date}-${row.member}-${index}`}>
                    <td>
                      <input
                        type="date"
                        disabled={!canEdit}
                        value={row.date}
                        onChange={(e) =>
                          setPreferredOwnership((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, date: e.target.value } : r,
                            ),
                          )
                        }
                        className="w-full min-w-[9rem] rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
                      />
                    </td>
                    <td>
                      <select
                        disabled={!canEdit}
                        value={row.member}
                        onChange={(e) =>
                          setPreferredOwnership((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, member: e.target.value as MemberCode } : r,
                            ),
                          )
                        }
                        className="rounded border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50"
                      >
                        {MEMBERS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        disabled={!canEdit}
                        value={row.share}
                        placeholder="0.0"
                        onChange={(e) =>
                          setPreferredOwnership((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, share: e.target.value } : r,
                            ),
                          )
                        }
                        className="w-28 rounded border border-gray-200 px-2 py-1 text-right text-sm tabular-nums disabled:bg-gray-50"
                      />
                    </td>
                    {canEdit ? (
                      <td>
                        <Button
                          type="button"
                          variant="ghost"
                          className="p-1 text-gray-500 hover:text-red-600"
                          onClick={() =>
                            setPreferredOwnership((prev) => prev.filter((_, i) => i !== index))
                          }
                          aria-label="Remove row"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setPreferredOwnership((prev) => [
                  ...prev,
                  { date: PREFERRED_OWNERSHIP_EARLIEST_DATE, member: 'JR', share: '1' },
                ])
              }
            >
              <PlusIcon className="h-4 w-4" />
              Add row
            </Button>
            <Button
              type="button"
              gradient
              onClick={() => void savePreferredOwnership()}
              disabled={!preferredOwnershipDirty || savingPreferredOwnership}
            >
              {savingPreferredOwnership ? 'Saving…' : 'Save preferred ownership'}
            </Button>
          </div>
        ) : null}
      </section>

      {syncPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay-standard)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Sync capital contributions from QuickBooks?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Review proposed changes before applying. Cancel to keep current settings.
            </p>

            {syncPreview.warnings.length > 0 && (
              <ul className="mt-4 space-y-2">
                {syncPreview.warnings.map((warning, index) => (
                  <li
                    key={index}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  >
                    {warning}
                  </li>
                ))}
              </ul>
            )}

            {syncPreview.logged_allocation_warning && (
              <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {syncPreview.logged_allocation_warning}
              </p>
            )}

            <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
              <table className="table-forecast min-w-full">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Member</th>
                    <th className="text-right">Current</th>
                    <th className="text-right">Proposed</th>
                    <th className="text-right">Delta</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {syncPreview.diff.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-sm text-gray-500">
                        No differences — QuickBooks matches current settings.
                      </td>
                    </tr>
                  ) : (
                    syncPreview.diff.map((row, index) => {
                      const isIgnored = row.ignored === true
                      const rowClassName = isIgnored ? 'opacity-60 text-gray-400' : undefined
                      return (
                        <tr key={`${row.date}-${row.member}-${index}`} className={rowClassName}>
                          <td>{row.date}</td>
                          <td>{row.member}</td>
                          <td className="text-right tabular-nums">
                            {currencyFmt.format(row.current)}
                          </td>
                          <td className="text-right tabular-nums">
                            {currencyFmt.format(row.proposed)}
                          </td>
                          <td
                            className={`text-right tabular-nums ${
                              !isIgnored && Math.abs(row.delta) > 0.005
                                ? 'text-amber-700'
                                : 'text-gray-400'
                            }`}
                          >
                            {!isIgnored && Math.abs(row.delta) > 0.005
                              ? currencyFmt.format(row.delta)
                              : '—'}
                          </td>
                          <td className="text-right">
                            {isIgnored ? (
                              <div className="flex items-center justify-end gap-2">
                                <span className="rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-500">
                                  Ignored
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={syncIgnoreActionLoading || syncApplying}
                                  onClick={() => void unignoreSyncRow(row.date, row.member)}
                                >
                                  Unignore
                                </Button>
                              </div>
                            ) : hasNonzeroProposedAmount(row.proposed) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={syncIgnoreActionLoading || syncApplying}
                                onClick={() =>
                                  void ignoreSyncRow(row.date, row.member as MemberCode, row.proposed)
                                }
                              >
                                Ignore
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={syncApplying || syncIgnoreActionLoading}
                onClick={() => setSyncPreview(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                gradient
                disabled={syncApplying || syncIgnoreActionLoading}
                onClick={() => void applySyncPreview()}
              >
                {syncApplying ? 'Applying…' : 'Confirm sync'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
